import { useState } from 'react'
import { NavLink, Route, Routes, Navigate, useNavigate } from 'react-router-dom'
import { StoreProvider, useStores } from './app/StoreContext'
import { isEmbed } from './api/client'
import { useMe } from './app/useMe'
import { applyTheme, currentTheme, nextTheme, themeLabel, type Theme } from './app/theme'
import { StorePicker } from './ui/StorePicker'
import { api, ApiError } from './api/client'
import { Button, ErrorBox } from './ui/primitives'

import { LoginPage, VerifyPage } from './features/auth/LoginPage'
import { PlanningPage } from './features/planning/PlanningPage'
import { DeliveryPage } from './features/planning/DeliveryPage'
import { DocumentReviewPage } from './features/documents/DocumentReviewPage'
import { ProposalsPage } from './features/proposals/ProposalsPage'
import { ProposalDetailPage } from './features/proposals/ProposalDetailPage'
import { BulkReviewPage } from './features/proposals/BulkReviewPage'
import { SpecialOrdersPage } from './features/specialOrders/SpecialOrdersPage'
import { DashboardPage } from './features/dashboard/DashboardPage'
import { ConfigPage } from './features/config/ConfigPage'

// La navigazione ricalca le voci dell'applicazione attuale, così chi la usa oggi ritrova
// i propri riferimenti. Cambia il modo, non il percorso mentale.
// Le voci con `permission` compaiono solo a chi ce l'ha: una scheda che risponde
// «non autorizzato» non è una funzione, è una porta chiusa in faccia.
const NAV: { to: string; label: string; permission?: string }[] = [
  { to: '/planning', label: 'Planning consegne' },
  { to: '/proposals', label: "Proposte d'ordine" },
  { to: '/special-orders', label: 'Ordini speciali' },
  { to: '/documents', label: 'Controllo documenti', permission: 'documents.review' },
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/config', label: 'Configurazioni', permission: 'config.read' },
]

export default function App() {
  return (
    <Routes>
      {/* Le pagine di accesso vivono fuori dal guscio: non hanno bisogno di punti vendita
          né di sessione, e devono aprirsi proprio quando la sessione non c'è. */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/verify" element={<VerifyPage />} />
      <Route
        path="*"
        element={
          <StoreProvider>
            <Shell />
          </StoreProvider>
        }
      />
    </Routes>
  )
}

function Shell() {
  const { loading, error, reload } = useStores()
  const { me, hasPermission } = useMe()
  const navigate = useNavigate()

  // Due manuali: quello completo per chi governa la logistica, quello del negozio per tutti
  // gli altri — senza le sezioni (e il login) che non li riguardano.
  const manuale = hasPermission('specialOrders.central') ? '/manuale.html' : '/manuale-negozio.html'

  const [theme, setTheme] = useState<Theme>(currentTheme)
  const cambiaTema = () => {
    const prossimo = nextTheme(theme)
    applyTheme(prossimo)
    setTheme(prossimo)
  }

  const esci = () => {
    void api
      .post('/auth/logout')
      .catch(() => {
        /* la sessione potrebbe essere già scaduta: si va al login comunque */
      })
      .finally(() => navigate('/login', { replace: true }))
  }

  // 401/403: non è un guasto, è l'assenza della sessione — si va al login di KPO.
  if (!loading && error instanceof ApiError && error.kind === 'forbidden')
    return <Navigate to="/login" replace />


  // Senza l'elenco dei punti vendita nessuna schermata può interrogare l'API: il filtro non
  // sarebbe affidabile. Va detto qui, una volta, invece di lasciare girare le pagine a vuoto.
  if (error && !loading)
    return (
      <div className="shell">
        <main className="content">
          <ErrorBox error={error} onRetry={reload} />
        </main>
      </div>
    )

  return (
    <>
      {/* Dentro KBE la cornice esterna ha gia il suo titolo e il suo utente: qui si toglie il
          doppione e si stringe la fascia, perche l'altezza dentro un iframe e tutta contesa. */}
      <div className={isEmbed() ? 'shell shell-incorniciata' : 'shell'}>
        <header className="topbar">
          <div className="brand">
            <img className="brand-logo" src="/favicon.svg" alt="" aria-hidden />
            <span className="brand-mark">KPO</span>
            <span className="brand-sub">Proposte Ordini e Consegne</span>
          </div>
          <div className="inline">
            <a
              className="topbar-link"
              href={manuale}
              target="_blank"
              rel="noreferrer"
              title="Il manuale d'uso, con le schermate: si apre in un'altra scheda"
            >
              Manuale
            </a>
            <StorePicker />
            <Button
              variant="ghost"
              onClick={cambiaTema}
              title="Tema dell'applicazione: Sistema segue il computer, Chiaro e Scuro lo forzano. La scelta resta su questo dispositivo."
            >
              Tema: {themeLabel[theme]}
            </Button>
            {me && (
              <>
                <span className="muted" title="Utente collegato">{me.name}</span>
                {/* Entrando da KBE non c'è un login da chiudere: la sessione embed scade da sé. */}
                {!isEmbed() && (
                  <Button variant="ghost" onClick={esci} title="Chiude la sessione di KPO su questo dispositivo">
                    Esci
                  </Button>
                )}
              </>
            )}
          </div>
        </header>

        <nav className="mainnav">
          {NAV.filter((item) => !item.permission || hasPermission(item.permission)).map((item) => (
            <NavLink key={item.to} to={item.to} className={({ isActive }) => (isActive ? 'active' : '')}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <main className="content">
          <Routes>
            <Route path="/" element={<Navigate to="/planning" replace />} />
            <Route path="/planning" element={<PlanningPage />} />
            <Route path="/planning/:id" element={<DeliveryPage />} />
            <Route path="/proposals" element={<ProposalsPage />} />
            <Route path="/proposals/bulk" element={<BulkReviewPage />} />
            <Route path="/proposals/:id" element={<ProposalDetailPage />} />
            <Route path="/special-orders" element={<SpecialOrdersPage />} />
            <Route path="/documents" element={<DocumentReviewPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/config" element={<ConfigPage />} />
            <Route path="*" element={<div className="empty">Pagina non trovata.</div>} />
          </Routes>
        </main>
      </div>
    </>
  )
}
